import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useObservable } from 'react-use-observable';

import { Select } from 'antd';

import { useFormikContext } from 'formik';
import type { IChildren, ISelectProduct } from 'v2/interfaces/product/selectProduct';
import productService from 'v2/services/selectProduct';

import type { ISelectProductMultipleHandles } from '.';

interface IProps {
  endpoint?: string;
  disabled?: boolean;
  label?: React.ReactNode;
  selected?: number[];
  onConfirm?: (product_ids: number[]) => void;
}

const MultipleSelectProductComponent: React.ForwardRefRenderFunction<ISelectProductMultipleHandles, IProps> = (
  { label, endpoint, disabled, selected, onConfirm: onConfirmProp }: IProps,
  ref
) => {
  const { setFieldValue, setStatus, values } = useFormikContext<{ product_id: number[] }>();
  const [isLoadingProducts] = useObservable(() => productService.isLoadingProducts(), []);

  useEffect(() => {
    return () => {
      productService.setProducts([]);
    };
  }, []);

  const [products] = useObservable(() => productService.loadDeliveries(endpoint), [endpoint]);

  const options = useMemo(() => {
    const items: Array<{ value: number; label: string }> = [];

    (products || []).forEach((product: ISelectProduct) => {
      items.push({ value: product.id, label: `${product.id} - ${product.title}` });

      (product.children || []).forEach((child: IChildren) => {
        items.push({ value: child.id, label: `${child.id} - ${child.title}` });
      });
    });

    return Array.from(new Map(items.map(item => [item.value, item])).values());
  }, [products]);

  const selectedIds = useMemo(() => {
    if (Array.isArray(selected)) {
      return selected;
    }

    return Array.isArray(values.product_id) ? values.product_id : [];
  }, [selected, values.product_id]);

  const onChange = useCallback(
    (nextSelected: number[]) => {
      setFieldValue('product_id', nextSelected);

      if (onConfirmProp) {
        onConfirmProp(nextSelected);
      }
    },
    [onConfirmProp, setFieldValue]
  );

  const unCheckAll = useCallback(() => {
    setFieldValue('product_id', []);

    if (onConfirmProp) {
      onConfirmProp([]);
    }
  }, [onConfirmProp, setFieldValue]);

  useEffect(() => {
    setStatus({
      clearSelectedProducts: () => unCheckAll()
    });
  }, [unCheckAll, setStatus]);

  useImperativeHandle(ref, () => {
    return {
      unCheckAll
    };
  });

  return (
    <>
      {label && (
        <label htmlFor='multiple-products' className='mb-1 block text-sm'>
          {label}
        </label>
      )}

      <Select
        id='multiple-products'
        className='w-full'
        mode='multiple'
        showSearch
        allowClear
        disabled={disabled}
        loading={isLoadingProducts}
        value={selectedIds}
        placeholder={isLoadingProducts ? 'Carregando seus produtos...' : 'Selecione seus produtos'}
        options={options}
        maxTagCount='responsive'
        notFoundContent={isLoadingProducts ? 'Carregando produtos...' : 'Nenhum produto encontrado'}
        filterOption={(input, option) => {
          const label = String(option?.label ?? '').toLowerCase();
          const value = String(option?.value ?? '').toLowerCase();
          const term = input.toLowerCase();

          return label.includes(term) || value.includes(term);
        }}
        onChange={values => onChange(values as number[])}
      />
    </>
  );
};

export const MultipleSelectProduct = forwardRef(MultipleSelectProductComponent);
